import React, { useCallback, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { PAYMENT_MERCHANT, PAYMENT_QR_IMAGE } from '../config/paymentMerchant';
import {
  formatUpiAmount,
  isUpiAmountEditable,
  parsePaymentAmount,
  resolvePaymentUpiLink,
} from '../utils/upiDeepLink';

function downloadFile(filename, href) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function paymentQrFilename(displayAmount) {
  const amount = parsePaymentAmount(displayAmount);
  const label = amount != null ? formatUpiAmount(amount).replace('.', '-') : 'payment';
  return `iconnect-upi-qr-${label}.png`;
}

/**
 * Dynamic UPI QR — encodes payee + exact amount so UPI apps pre-fill the payment amount.
 * Falls back to static merchant QR only when UPI ID or amount is missing.
 * Includes Download so members can open the image from GPay/PhonePe Gallery on the same phone.
 */
function PaymentUpiQrCode({
  displayAmount,
  planName,
  size = 224,
  className = '',
  showAmountHint = true,
  showDownload = true,
  downloadButtonLabel = 'Download QR image',
  downloadButtonPrimary = false,
}) {
  const canvasWrapRef = useRef(null);
  const [downloadMsg, setDownloadMsg] = useState('');
  const upiLink = useMemo(
    () => resolvePaymentUpiLink({ displayAmount, planName }),
    [displayAmount, planName]
  );
  const amountValue = useMemo(() => parsePaymentAmount(displayAmount), [displayAmount]);
  const filename = useMemo(() => paymentQrFilename(displayAmount), [displayAmount]);

  const handleDownloadDynamic = useCallback(() => {
    setDownloadMsg('');
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) {
      setDownloadMsg('Unable to download QR. Please try again.');
      return;
    }
    try {
      const href = canvas.toDataURL('image/png');
      downloadFile(filename, href);
      setDownloadMsg('QR saved. Open your UPI app → Scan → Gallery → select this image.');
    } catch {
      setDownloadMsg('Unable to download QR. Please try again.');
    }
  }, [filename]);

  const handleDownloadStatic = useCallback(async () => {
    setDownloadMsg('');
    try {
      const res = await fetch(PAYMENT_QR_IMAGE);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      downloadFile(filename, href);
      URL.revokeObjectURL(href);
      setDownloadMsg('QR saved. Open your UPI app → Scan → Gallery → select this image.');
    } catch {
      // Fallback: open image (user can long-press save on some browsers)
      downloadFile(filename, PAYMENT_QR_IMAGE);
      setDownloadMsg('If download did not start, long-press the QR and save the image.');
    }
  }, [filename]);

  if (!upiLink) {
    return (
      <div className={`text-center ${className}`.trim()}>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 inline-block">
          <img
            src={PAYMENT_QR_IMAGE}
            alt={`Scan to pay ${PAYMENT_MERCHANT.payeeName}`}
            className="mx-auto object-contain"
            style={{ width: size, height: size }}
          />
        </div>
        <p className="mt-3 text-xs text-amber-900 leading-snug max-w-xs mx-auto">
          Fixed amount QR is unavailable. Enter{' '}
          <strong>{displayAmount || 'the exact plan amount'}</strong> manually when paying.
        </p>
        {showDownload && (
          <div className="mt-3">
            <button
              type="button"
              onClick={handleDownloadStatic}
              className={
                downloadButtonPrimary
                  ? 'w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-indigo-300/40 active:scale-[0.99]'
                  : 'inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-800 shadow-sm hover:bg-slate-50 active:scale-[0.99]'
              }
            >
              {downloadButtonLabel}
            </button>
            {downloadMsg ? (
              <p className="mt-2 text-[11px] text-slate-600 leading-snug max-w-xs mx-auto">
                {downloadMsg}
              </p>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`text-center ${className}`.trim()}>
      <div
        ref={canvasWrapRef}
        className="rounded-2xl border border-slate-200 bg-white p-4 inline-block"
      >
        <QRCodeCanvas
          value={upiLink}
          size={size}
          level="M"
          includeMargin
          aria-label={`UPI QR code to pay ${displayAmount} to ${PAYMENT_MERCHANT.payeeName}`}
        />
      </div>
      {showAmountHint && amountValue != null && (
        <p className="mt-3 text-xs text-slate-600 leading-snug max-w-sm mx-auto">
          {isUpiAmountEditable() ? (
            <>
              Test mode: amount is <strong className="text-slate-800">editable</strong> in the UPI
              app. Enter the plan amount (e.g. ₹{formatUpiAmount(amountValue)}) for{' '}
              <strong className="text-slate-800">{PAYMENT_MERCHANT.payeeName}</strong> before paying.
            </>
          ) : (
            <>
              Scanning this QR opens your UPI app with{' '}
              <strong className="text-slate-800">₹{formatUpiAmount(amountValue)}</strong> already set
              for <strong className="text-slate-800">{PAYMENT_MERCHANT.payeeName}</strong>. Do not
              change the amount before paying.
            </>
          )}
        </p>
      )}
      {showDownload && (
        <div className="mt-3">
          <button
            type="button"
            onClick={handleDownloadDynamic}
            className={
              downloadButtonPrimary
                ? 'w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-indigo-300/40 active:scale-[0.99]'
                : 'inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs font-bold text-indigo-900 shadow-sm hover:bg-indigo-100 active:scale-[0.99]'
            }
          >
            {downloadButtonLabel}
          </button>
          {!downloadButtonPrimary ? (
            <p className="mt-2 text-[11px] text-slate-500 leading-snug max-w-sm mx-auto">
              Same phone: Download → UPI app → Scan → Gallery → pay.
            </p>
          ) : null}
          {downloadMsg ? (
            <p className="mt-2 text-[11px] font-semibold text-emerald-800 leading-snug max-w-sm mx-auto">
              {downloadMsg}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default PaymentUpiQrCode;
